import type { Router } from 'vue-router'
import { hasActiveModal } from './useModalBackHandler'
import { useSwipeDrawer } from './useSwipeDrawer'

export const DRAWER_BASE_PAGES = [
  '/recipes',
  '/shopping-list',
  '/articles',
  '/article-categories',
  '/recipe-categories',
  '/units',
  '/support',
  '/settings',
  '/trash'
] as const

export function getDrawerPages(isLoggedIn: boolean): string[] {
  const pages: string[] = [...DRAWER_BASE_PAGES]
  if (isLoggedIn) {
    pages.push('/user')
  }
  return pages
}

export function getCurrentPageIndex(currentPath: string, pages: string[]): number {
  const cleanPath = (currentPath || '').split('?')[0].split('#')[0]

  if (!cleanPath || cleanPath === '/') {
    return pages.indexOf('/recipes')
  }
  if (cleanPath.startsWith('/recipe/')) {
    return pages.indexOf('/recipes')
  }
  if (cleanPath === '/categories') {
    return pages.indexOf('/article-categories')
  }
  for (let i = 0; i < pages.length; i++) {
    const page = pages[i]
    if (cleanPath === page || cleanPath.startsWith(page + '/')) {
      return i
    }
  }
  return -1
}

const OVERLAY_SELECTORS = [
  '[role="dialog"]',
  '[aria-modal="true"]',
  '.modal-overlay',
  '.legal-modal-overlay',
  '.popup-overlay',
  '.confirm-modal-overlay',
  '.barcode-scanner-overlay',
  '.clear-data-overlay',
  '.delete-account-overlay',
  '.search-overlay',
  '.settings-search-overlay',
  '.settings-search-modal',
  '.cropper-overlay',
  '.modal-backdrop',
  '.dialog-backdrop',
  '.popup-backdrop',
  '.sheet-backdrop',
  '.popup-add-to-cart',
  '.popup-recipe-comment',
  '.scan-overlay',
  '.modal-container',
  '.dialog-container'
].join(', ')

export function isDrawerOverlaid(route?: any, store?: any): boolean {
  // 1. Modals tracked via useModalBackHandler
  if (hasActiveModal()) return true

  // 2. Global search or notifications modal open
  if (store?.isGlobalSearchOpen || store?.isNotificationsModalOpen) return true

  // 3. Isolated pages or routes where drawer is hidden
  const path = route?.path || (typeof window !== 'undefined' ? window.location.pathname : '')
  if (path === '/login' || (path.startsWith('/recipe/') && path.endsWith('/print')) || path.startsWith('/print/')) {
    return true
  }

  // 4. Overlays, popups or dialogs in DOM
  if (typeof document !== 'undefined') {
    if (document.querySelector(OVERLAY_SELECTORS)) {
      return true
    }
  }

  return false
}

export function handlePageNavigationKeyDown(
  e: KeyboardEvent,
  router: Router,
  authStore: any,
  store: any
): boolean {
  if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return false

  const isPageDown = e.key === 'PageDown' || e.code === 'PageDown'
  const isPageUp = e.key === 'PageUp' || e.code === 'PageUp'
  if (!isPageDown && !isPageUp) return false

  if (e.repeat) {
    e.preventDefault()
    return true
  }

  // If focus is inside a multi-line textarea, allow native text paging
  if (typeof document !== 'undefined') {
    const activeEl = document.activeElement as HTMLElement | null
    if (activeEl && activeEl.tagName === 'TEXTAREA') {
      return false
    }
  }

  // Check if drawer is overlaid (e.g. popup/dialog is open)
  if (isDrawerOverlaid(router?.currentRoute?.value, store)) {
    return false
  }

  e.preventDefault()

  const isLoggedIn = !!authStore?.user
  const pages = getDrawerPages(isLoggedIn)
  const currentPath = router?.currentRoute?.value?.path || ''
  const currentIndex = getCurrentPageIndex(currentPath, pages)

  let nextIndex: number
  if (isPageDown) {
    // "Bild runter": move down/forward in the drawer menu
    nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % pages.length
  } else {
    // "Bild hoch": move up/backward in the drawer menu
    nextIndex = currentIndex === -1 ? pages.length - 1 : (currentIndex - 1 + pages.length) % pages.length
  }

  const targetPage = pages[nextIndex]
  if (targetPage && targetPage !== currentPath) {
    try {
      useSwipeDrawer().closeSidebar()
    } catch {}
    router?.push?.(targetPage)?.catch?.(() => {})
  }

  return true
}
